export const POWER_AUTOMATE_BATCH_LIMIT = 20;
export const POWER_AUTOMATE_BATCH_URL = 'https://api.flow.microsoft.com/batch';

export interface PowerAutomateBatchRequest {
  id?: string;
  url: string;
  headers?: Record<string, string>;
}

export interface PowerAutomateBatchJsonRequest {
  httpMethod: 'GET';
  relativeUrl: string;
  headers?: Record<string, string>;
}

export interface PowerAutomateBatchResponse {
  id: string;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  bodyText: string;
  body: any;
  ok: boolean;
}

export function isPowerAutomateFlowNotFoundResponse(response: Pick<PowerAutomateBatchResponse, 'status' | 'body'> | null | undefined): boolean {
  return Number(response?.status || 0) === 404
    && String(response?.body?.error?.code || '') === 'FlowNotFound';
}

export function buildPowerAutomateGetBatchBody(requests: PowerAutomateBatchRequest[]): {
  requests: PowerAutomateBatchJsonRequest[];
} {
  return {
    requests: (requests || []).map((request) => ({
      httpMethod: 'GET',
      relativeUrl: toPowerAutomateRelativeUrl(request.url),
      ...(request.headers ? { headers: request.headers } : {}),
    })),
  };
}

export function parsePowerAutomateBatchResponse(
  body: string | { responses?: any[] },
  requests: PowerAutomateBatchRequest[] = [],
): PowerAutomateBatchResponse[] {
  const parsed = typeof body === 'string' ? parseJson(body) : body;
  const responses = Array.isArray(parsed?.responses) ? parsed.responses : null;
  if (!responses) {
    throw new Error('Power Automate batch response did not include a responses array');
  }

  return responses.map((response, index) => {
    const request = requests[index];
    const status = Number(response?.httpStatusCode || response?.status || 0);
    const content = response?.content ?? response?.body ?? null;
    return {
      id: request?.id || String(index + 1),
      status,
      statusText: String(response?.statusText || ''),
      headers: normalizeHeaders(response?.headers),
      bodyText: content == null ? '' : (typeof content === 'string' ? content : JSON.stringify(content)),
      body: content,
      ok: status >= 200 && status < 300,
    };
  });
}

export function chunkPowerAutomateBatchRequests<T>(items: T[], limit = POWER_AUTOMATE_BATCH_LIMIT): T[][] {
  const size = Math.max(1, Math.min(limit || POWER_AUTOMATE_BATCH_LIMIT, POWER_AUTOMATE_BATCH_LIMIT));
  const chunks: T[][] = [];
  for (let i = 0; i < (items || []).length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function parseJson(body: string): any {
  try {
    return JSON.parse(body || '{}');
  } catch {
    throw new Error('Power Automate batch response was not valid JSON');
  }
}

function normalizeHeaders(headers: any): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers || {})) {
    result[key.toLowerCase()] = String(value ?? '');
  }
  return result;
}

function toPowerAutomateRelativeUrl(url: string): string {
  const parsed = new URL(url, 'https://api.flow.microsoft.com');
  parsed.searchParams.delete('api-version');
  const query = parsed.searchParams.toString();
  return `${parsed.pathname}${query ? `?${query}` : ''}`;
}
